
import { UseFormReturn } from 'react-hook-form';
import { useCallback } from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { SimpleCitySelector } from '@/components/simple-city-selector';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/lib/context';
import { getTranslations } from '@/lib/translations';

interface Step2LocationProps {
  form: UseFormReturn<any>;
}

export function Step2Location({ form }: Step2LocationProps) {
  const { user } = useApp();
  const t = getTranslations(user?.language || 'en');

  const handleCitySelected = useCallback((city: string, latitude: string, longitude: string) => {
    console.log('[Step2Location] 🎯 handleCitySelected called:', { city, latitude, longitude });
    try {
      const locationValue = {
        city: city,
        latitude: latitude,
        longitude: longitude
      };
      console.log('[Step2Location] 📝 Setting location value:', locationValue);

      form.setValue("location", locationValue, {
        shouldValidate: true,
        shouldDirty: true
      });

      console.log('[Step2Location] ✅ form.setValue completed');

      // Verifica immediata del valore
      const currentValue = form.getValues('location');
      console.log('[Step2Location] 📊 Current location value after setValue:', currentValue);

      // Forza un re-render del form
      form.trigger('location');

    } catch (error) {
      console.error('[Step2Location] ❌ Error in handleCitySelected:', error);
      // Non fare throw - solo log l'errore
    }
  }, [form]);

  const getTitle = () => {
    switch (user?.language) {
      case 'it': return 'Luogo e Dettagli';
      case 'es': return 'Ubicación y Detalles';
      case 'de': return 'Ort & Details';
      case 'pt': return 'Local e Detalhes';
      case 'fr': return 'Lieu et Détails';
      default: return 'Location & Details';
    }
  };

  const getSubtitle = () => {
    switch (user?.language) {
      case 'it': return 'Seleziona la città e aggiungi una descrizione per il tuo speed date';
      case 'es': return 'Selecciona la ciudad y añade una descripción para tu speed date';
      case 'de': return 'Wähle die Stadt und füge eine Beschreibung für dein Speed Date hinzu';
      case 'pt': return 'Selecione a cidade e adicione uma descrição para o seu speed date';
      case 'fr': return 'Sélectionnez la ville et ajoutez une description pour votre speed date';
      default: return 'Select the city and add a description for your speed date';
    }
  };

  const getCityLabel = () => {
    switch (user?.language) {
      case 'it': return 'Città';
      case 'es': return 'Ciudad';
      case 'de': return 'Stadt';
      case 'pt': return 'Cidade';
      case 'fr': return 'Ville';
      default: return 'City';
    }
  };

  const getCityPlaceholder = () => {
    switch (user?.language) {
      case 'it': return 'Cerca una città...';
      case 'es': return 'Busca una ciudad...';
      case 'de': return 'Suche eine Stadt...';
      case 'pt': return 'Procure uma cidade...';
      case 'fr': return 'Rechercher une ville...';
      default: return 'Search for a city...';
    }
  };

  const getDescriptionLabel = () => {
    switch (user?.language) {
      case 'it': return 'Descrizione';
      case 'es': return 'Descripción';
      case 'de': return 'Beschreibung';
      case 'pt': return 'Descrição';
      case 'fr': return 'Description';
      default: return 'Description';
    }
  };

  const getDescriptionPlaceholder = () => {
    switch (user?.language) {
      case 'it': return 'Descrivi il tuo speed date: dettagli sul luogo, cosa aspettarsi, dress code, ecc...';
      case 'es': return 'Describe tu speed date: detalles del lugar, qué esperar, código de vestimenta, etc...';
      case 'de': return 'Beschreibe dein Speed Date: Standortdetails, was zu erwarten ist, Dresscode, usw...';
      case 'pt': return 'Descreva seu speed date: detalhes do local, o que esperar, código de vestimenta, etc...';
      case 'fr': return 'Décrivez votre speed date : détails du lieu, à quoi s\'attendre, code vestimentaire, etc...';
      default: return 'Describe your speed date: location details, what to expect, dress code, etc...';
    }
  };

  const getCharacterCount = () => {
    switch (user?.language) {
      case 'it': return 'caratteri (min. 10)';
      case 'es': return 'caracteres (mín. 10)';
      case 'de': return 'Zeichen (mind. 10)';
      case 'pt': return 'caracteres (mín. 10)';
      case 'fr': return 'caractères (min. 10)';
      default: return 'characters (min. 10)';
    }
  };

  const getTipsTitle = () => {
    switch (user?.language) {
      case 'it': return 'Consigli sulla Location';
      case 'es': return 'Consejos de Ubicación';
      case 'de': return 'Standort-Tipps';
      case 'pt': return 'Dicas de Localização';
      case 'fr': return 'Conseils de Localisation';
      default: return 'Location Tips';
    }
  };

  const getTipsDescription = () => {
    switch (user?.language) {
      case 'it': return 'Scegli una location centrale e descrivi cosa rende speciale il tuo speed date!';
      case 'es': return '¡Elige una ubicación céntrica y describe qué hace especial tu speed date!';
      case 'de': return 'Wähle einen zentralen Ort und beschreibe, was dein Speed Date besonders macht!';
      case 'pt': return 'Escolha um local central e descreva o que torna seu speed date especial!';
      case 'fr': return 'Choisissez un lieu central et décrivez ce qui rend votre speed date spécial !';
      default: return 'Choose a central location and describe what makes your speed date special!';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold tg-text mb-2">{getTitle()}</h2>
        <p className="text-sm tg-hint">{getSubtitle()}</p>
      </div>

      <FormField
        control={form.control}
        name="location"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="tg-text">{getCityLabel()}</FormLabel>
            <FormControl>
              <SimpleCitySelector
                onCitySelected={handleCitySelected}
                placeholder={getCityPlaceholder()}
                defaultCity={field.value?.city || ""}
                registrationMode={true}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="tg-text">{getDescriptionLabel()}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ''}
                placeholder={getDescriptionPlaceholder()}
                className="tg-secondary-bg text-white  min-h-[100px] resize-none placeholder:text-[#858585]"
                maxLength={500}
              />
            </FormControl>
            <div className="text-xs tg-hint text-right">
              {(field.value || '').length}/500 {getCharacterCount()}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="mt-4 p-4 rounded-lg border border-[#353535]" style={{
        background: 'radial-gradient(circle at bottom right, #1025eb 0%, var(--tg-theme-secondary-bg-color, #252525) 70%)'
      }}>
        <h3 className="font-medium tg-text mb-2 flex items-center gap-2">
          <img
            src="https://em-content.zobj.net/source/telegram/386/light-bulb_1f4a1.webp"
            alt="💡"
            className="w-5 h-5"
          />
          {getTipsTitle()}
        </h3>
        <p className="text-sm tg-hint">
          {getTipsDescription()}
        </p>
      </div>
    </div>
  );
}
